<template>
  <section
    class="course-catalog-section text-white py-6 md:py-12 px-6 myt-12 mb-24 md:px-12 bg-gradient-to-br from-[#1778FF] to-[#1627A891]"
  >
    <!-- Header -->
    <div class="mb-6">
      <button
        @click="navigateTo('/formaciones')"
        class="bg-[#E8F6FF] text-[#0554F2] text-sm font-bold font-poppins px-4 py-2 rounded-full mb-4"
      >
        Todos los cursos
      </button>
      <h2
        class="text-xl md:text-3xl font-extrabold font-poppins text-[#F6F7F9]"
      >
        Encuentra el curso que mas se adapta a tus necesidades
      </h2>
    </div>

    <div class="flex flex-col md:flex-row gap-6">
      <div class="md:hidden mb-6 grid grid-cols-1 gap-3">
        <!-- Área select -->
        <select
          v-model="areaActive"
          class="rounded-full px-3 py-2 text-sm text-black bg-[#F6F6F6] font-poppins"
        >
          <option :value="''">Todas las áreas</option>
          <option v-for="area in areas" :key="area" :value="area">
            {{ area }}
          </option>
        </select>

        <!-- Filtro select -->
        <select
          v-model="filterActive"
          class="rounded-full px-3 py-2 text-sm text-black bg-[#F6F6F6] font-poppins"
        >
          <option v-for="filter in filters" :key="filter" :value="filter">
            {{ filter }}
          </option>
        </select>
      </div>
      <!-- Sidebar Filters -->
      <aside
        class="hidden md:flex flex-col gap-3 border-b md:border-e-2 md:border-b-0 pb-4 md:pe-4"
      >
        <span class="font-bold font-poppins">Áreas</span>
        <button
          v-for="area in areas"
          :key="area"
          class="rounded-full px-4 py-2 mb-2 text-sm font-bold font-poppins transition-all delay-75 w-full"
          @click="setCategory(area)"
          :class="
            areaActive === area
              ? 'bg-[#07399D] border-[#07399D] text-white '
              : 'bg-white border-white hover:bg-[#07399D] hover:border-[#07399D] hover:text-white text-[#343434]'
          "
        >
          {{ area }}
        </button>
      </aside>

      <!-- Main Content -->
      <div class="flex-1 overflow-hidden">
        <!-- Filter Chips -->
        <div class="flex-wrap gap-3 mb-6 hidden md:flex">
          <button
            v-for="filter in filters"
            :key="filter"
            @click="setFilter(filter)"
            class="border-2 text-white font-poppins rounded-full px-4 py-2 text-sm font-bold transition-all delay-75"
            :class="
              filterActive == filter
                ? 'bg-[#07399D] border-[#07399D]'
                : 'bg-transparent border-white hover:bg-[#07399D] hover:border-[#07399D]'
            "
          >
            {{ filter }}
          </button>
        </div>

        <ClientOnly>
          <Carousel
            v-if="!loader"
            class="overflow-hidden"
            ref="swiperRef"
            :key="areaActive + filterActive"
            :wrap-around="false"
            :mouse-wheel="false"
            :mouse-drag="false"
            :touch-drag="false"
            :gap="15"
            :transition="500"
            easing="ease-in-out"
            snapAlign="start"
            :itemsToScroll="1"
            breakpointMode="carousel"
            :breakpoints="{
              0: { itemsToShow: 1.2 },
              640: { itemsToShow: 2 },
              768: { itemsToShow: 2 },
              1024: { itemsToShow: 3 },
              1280: { itemsToShow: 4 },
            }"
          >
            <Slide v-for="course in courses" :key="course.id">
              <HomeSectionsCourseCard2
                :id="course.id"
                :title="course.title"
                :desc="course.description.slice(0, 130) + '...'"
                :rating="course.review"
                :img="course.imageUrl"
              />
            </Slide>
          </Carousel>
          <UiLoading v-else class="mx-auto" />
        </ClientOnly>

        <!-- Ver Todo Button -->
        <div class="mt-6 flex justify-between items-center">
          <div>
            <button
              @click="navigateTo('/formaciones')"
              class="border border-white text-white rounded-full px-6 py-2 hover:bg-white hover:text-[#2e52e6] transition font-bold"
            >
              Ver Todo
            </button>
          </div>
          <div class="flex justify-end gap-4 bottom">
            <button
              @click="prev()"
              class="rounded-full left bg-[#e7e7e7] hover:shadow-sm hover:bg-[#d5d5d5]"
            >
              <img :src="leftButton" alt="left" /></button
            ><button
              @click="next()"
              class="right bg-blue hover:bg-[#0d5aff] hover:shadow-sm rounded-full"
            >
              <img :src="rightButton" alt="right" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Carousel, Slide } from "vue3-carousel";
import leftButton from "@/assets/img/icon/left-arrow.svg";
import rightButton from "@/assets/img/icon/right-arrow.svg";
import type { Category } from "~/types/Categories";
import type { Course } from "~/types/Cours";
import "vue3-carousel/dist/carousel.css";

const loader = ref(false);
const { getCourses } = useCoursApi();
const { categories } = useDataStore();

const courses = ref<Course[]>([]);

// UI state
const areaActive = ref<string>(""); // category
const filterActive = ref<string>("Todo"); // filter
const swiperRef = ref<any>(null);

// Areas list
const areas = computed<string[]>(() => [
  ...(categories ?? []).map((c: Category) => c.name),
]);

// Carousel controls
const prev = () => swiperRef.value?.prev?.();
const next = () => swiperRef.value?.next?.();

// ---- Build the query ONCE here ----
const buildQuery = (category?: string, filter?: string) => {
  const p = new URLSearchParams();

  // Category
  if (category) p.set("categoryName", category);

  // Filters
  switch (filter) {
    case "Todo":
    case undefined:
    case "":
      break;

    case "+ 5 Módulos":
      p.set("modulesCount[gte]", "5");
      break;

    case "- 5 Módulos":
      p.set("modulesCount[lt]", "5");
      break;

    case "Duración - 4 horas":
      p.set("duration[lte]", "4");
      break;

    case "Duración + 4 horas":
      p.set("duration[gte]", "4");
      break;

    case "Presencial":
      p.set("modality", "presencial");
      break;

    case "En linea":
      p.set("modality", "en_linea");
      break;

    case "Hibrido":
      p.set("modality", "hibrido");
      break;
  }

  return p.toString();
};

// Fetch with combined filters
const getData = async (category?: string, filter?: string) => {
  try {
    loader.value = true;
    const query = buildQuery(category, filter);
    const data = await getCourses(query ? `${query}` : "");
    courses.value = data?.member ?? [];
  } catch (err) {
    // You can add a toast or console here if you want
    // console.error(err);
  } finally {
    loader.value = false;
  }
};

// Handlers
const setCategory = (area: string) => {
  // toggle behavior: click again to clear
  areaActive.value = areaActive.value === area ? "" : area;
};

const setFilter = (filter: string) => {
  filterActive.value = filter;
};

// React to BOTH area and filter
watch(
  [areaActive, filterActive],
  ([area, filter]) => {
    getData(area, filter);
  },
  { immediate: true }
);

// Static list of filters
const filters = [
  "Todo",
  "+ 5 Módulos",
  "- 5 Módulos",
  "Duración - 4 horas",
  "Duración + 4 horas",
  "Presencial",
  "En linea",
  "Hibrido",
];
</script>

<style scoped>
.bottom button {
  width: 56px;
  height: 56px;
  transition: all 0.3s ease;
}
</style>
